// Files + Repos — telas 16 e 17 (docs/screens/16-files.md, 17-repos.md).
// Dono do dado: o repo GitHub do projeto. Files é a contents API por inteiro
// (listar, ler, salvar, apagar); histórico e autoria vêm dos commits. Repos é
// GET /user/repos, e a aba Environment lê os Actions Secrets. Nenhum storage
// próprio — a pasta .versions do AgentOS aqui é o git nativo.

import { repoAtual, gateway, github, SourceError, type Repo } from "../api";
import { empty, errorBox, esc, head, md, skeleton, table, tabs, toast } from "../ui";

// ------------------------------------------------------------------- estilo

const CSS = `
  .files-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
  .files-bar .crumbs { margin-bottom: 0; }
  .files-bar .back { margin-bottom: 0; }
  .files-scope { display: inline-flex; gap: 6px; margin-right: 8px; }
  .files-scope-pill { font-family: var(--mono); font-size: 11px; font-weight: 600; padding: 4px 12px; border-radius: 12px; border: 1px solid var(--rule); background: none; color: var(--ink-2); cursor: default; }
  .files-scope-pill[aria-selected="true"] { background: var(--accent); border-color: var(--accent); color: #12130E; }
  .files-scope-pill:disabled { opacity: .45; }
  .files-copy { background: none; border: none; color: var(--ink-3); cursor: pointer; font-size: 13px; padding: 2px 4px; }
  .files-copy:hover { color: var(--ink); }
  .files-acoes { margin-left: auto; display: flex; gap: 8px; }
  .files-meta { font-family: var(--mono); font-size: 11px; color: var(--ink-3); margin: -6px 0 14px; }
  .files-dirty { color: var(--accent); margin-left: 5px; }
  .files-menu { display: inline-block; }
  .files-menu summary { list-style: none; cursor: pointer; color: var(--ink-3); font-family: var(--mono); padding: 0 6px; user-select: none; }
  .files-menu summary::-webkit-details-marker { display: none; }
  .files-menu summary:hover { color: var(--ink); }
  .files-menu-list { position: fixed; z-index: 20; min-width: 130px; display: flex; flex-direction: column; background: var(--surface-2); border: 1px solid var(--rule); border-radius: var(--r); padding: 4px; box-shadow: 0 10px 30px -10px rgba(0,0,0,.6); }
  .files-menu-list a, .files-menu-list button { text-align: left; font-family: var(--mono); font-size: 12px; color: var(--ink); background: none; border: none; border-radius: 4px; padding: 6px 10px; cursor: pointer; text-decoration: none; }
  .files-menu-list a:hover, .files-menu-list button:hover { background: var(--rule-2); text-decoration: none; }
  .files-editor { display: grid; grid-template-columns: auto 1fr; border: 1px solid var(--rule); border-radius: var(--r); background: var(--bg); height: calc(100vh - 250px); min-height: 320px; overflow: hidden; }
  .files-gutter { margin: 0; padding: 12px 10px; text-align: right; color: var(--ink-3); user-select: none; overflow: hidden; font-family: var(--mono); font-size: 12.5px; line-height: 1.5; border-right: 1px solid var(--rule-2); }
  .files-ta { border: none; background: transparent; resize: none; outline: none; padding: 12px 14px; font-family: var(--mono); font-size: 12.5px; line-height: 1.5; color: var(--ink); white-space: pre; overflow: auto; }
  .files-panel { max-width: 640px; }
  .files-env-note { font-size: 12.5px; color: var(--ink-2); margin: 0 0 14px; }
  .files-env-empty { text-align: center; color: var(--ink-3); font-size: 13px; padding: 36px 0; }
  .files-repo-name { color: var(--ink); font-weight: 600; }
`;
if (!document.getElementById("css-files")) {
  const st = document.createElement("style");
  st.id = "css-files";
  st.textContent = CSS;
  document.head.append(st);
}

const ICO_PASTA = `<svg class="file-icon" style="color:var(--accent)" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M1.8 4.2h4l1.4 1.8h7v6.6a1 1 0 0 1-1 1H2.8a1 1 0 0 1-1-1V4.2z"/></svg>`;
const ICO_ARQ = `<svg class="file-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M4 1.8h5.2l2.8 2.8v9.6H4V1.8z"/><path d="M9.2 1.8v2.8H12"/></svg>`;

// ------------------------------------------------------------------- formas

/** Item da contents API. `github.contents` da api.ts devolve menos campos do
 *  que esta tela precisa (sha para o Save/Delete, content para o editor), então
 *  aqui o GET é direto pelo gateway, com a forma completa. */
interface FsEntry {
  name: string;
  path: string;
  type: "file" | "dir";
  size: number;
  sha: string;
  encoding?: string;
  content?: string;
  download_url: string | null;
}

interface FsCommit {
  commit: { committer: { date: string }; author: { name: string } };
  author: { login: string } | null;
}

// --------------------------------------------------------------- utilidades

/** Base64 da API de contents, com UTF-8 correto — `atob` sozinho quebra acento. */
const deB64 = (s: string): string =>
  new TextDecoder().decode(Uint8Array.from(atob(s.replace(/\s/g, "")), (c) => c.charCodeAt(0)));

const paraB64 = (s: string): string =>
  btoa(Array.from(new TextEncoder().encode(s), (b) => String.fromCharCode(b)).join(""));

/** Datas no formato do app de referência: "Aug 13, 2026". */
const fmtData = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "–";

const kb = (n: number): string => `${(n / 1024).toFixed(1)} KB`;

const junta = (...ps: string[]): string => ps.filter(Boolean).join("/");

const subRota = (raiz: string): string =>
  decodeURIComponent(location.hash.replace(/^#/, "")).replace(raiz, "").replace(/^\//, "");


/** PUT na contents API. Com `sha`, atualiza; sem, cria. Devolve o sha novo —
 *  o GitHub responde 409/422 se o arquivo mudou por baixo, proteção de graça. */
async function escreve(repo: string, path: string, contentB64: string, sha?: string): Promise<string> {
  const r = await gateway<{ content: { sha: string } | null }>(`github/repos/${repo}/contents/${path}`, {
    method: "PUT",
    body: JSON.stringify({ message: `HOPE: salva ${path}`, content: contentB64, ...(sha ? { sha } : {}) }),
  });
  return r.content?.sha ?? "";
}

const apaga = (repo: string, path: string, sha: string, message: string): Promise<unknown> =>
  gateway<unknown>(`github/repos/${repo}/contents/${path}`, {
    method: "DELETE",
    body: JSON.stringify({ message, sha }),
  });

/** Envolve uma escrita disparada por botão: desabilita durante o envio e, se
 *  falhar, mostra o motivo e devolve o botão. No sucesso quem re-renderiza é
 *  o chamador — o botão morre junto com o HTML antigo. */
async function agir(btn: HTMLButtonElement, fn: () => Promise<void>): Promise<void> {
  btn.disabled = true;
  try {
    await fn();
  } catch (e) {
    toast((e as SourceError).message);
    btn.disabled = false;
  }
}

/** Menus "···"/"⋮" são <details> nativos; a lista é `position: fixed` para não
 *  ser decepada pelo overflow da tabela — só falta dizer onde abrir. */
function posicionaMenus(el: HTMLElement): void {
  for (const dt of el.querySelectorAll<HTMLDetailsElement>(".files-menu")) {
    dt.ontoggle = () => {
      const listEl = dt.querySelector<HTMLElement>(".files-menu-list");
      if (!dt.open || !listEl) return;
      const r = dt.getBoundingClientRect();
      listEl.style.top = `${r.bottom + 4}px`;
      listEl.style.left = `${Math.max(8, r.right - 140)}px`;
    };
  }
}

// ------------------------------------------------------------ Files: trilhas

function crumbLista(caminho: string): string {
  const partes = caminho ? caminho.split("/") : [];
  const pedacos = [
    partes.length
      ? `<a class="crumb" href="#/arquivos">Root</a>`
      : `<span class="crumb crumb-now">Root</span>`,
    ...partes.map((p, i) =>
      i === partes.length - 1
        ? `<span class="crumb crumb-now">${esc(p)}</span>`
        : `<a class="crumb" href="#/arquivos/${esc(partes.slice(0, i + 1).join("/"))}">${esc(p)}</a>`,
    ),
  ];
  return `<nav class="crumbs">${pedacos.join('<span class="crumb-sep">›</span>')}</nav>`;
}

/** Trilha do viewer: "Files › pasta › … › arquivo ●". Todo segmento navega
 *  (os além de "Files" são INFERIDO no spec) e leva o guard de edição suja. */
function crumbViewer(path: string): string {
  const partes = path.split("/");
  const nomeArq = partes[partes.length - 1];
  const pedacos = [
    `<a class="crumb files-guard" href="#/arquivos">Files</a>`,
    ...partes.slice(0, -1).map(
      (p, i) =>
        `<a class="crumb files-guard" href="#/arquivos/${esc(partes.slice(0, i + 1).join("/"))}">${esc(p)}</a>`,
    ),
    `<span class="crumb crumb-now">${esc(nomeArq)}<span class="files-dirty" id="files-dot" hidden>●</span></span>`,
  ];
  return `<nav class="crumbs">${pedacos.join('<span class="crumb-sep">›</span>')}</nav>`;
}

// ------------------------------------------------------------------- Files

/** Rotas: #/arquivos e #/arquivos/<caminho>. Um GET decide a tela: a contents
 *  API devolve array para diretório e objeto para arquivo — lista ou viewer. */
export const files = async (el: HTMLElement): Promise<void> => {
  const caminho = subRota("/arquivos");
  el.innerHTML = head({ title: "Files", sub: "Browse and manage stored files" }) + skeleton();
  try {
    const repo = await repoAtual();
    const r = await gateway<FsEntry[] | FsEntry>(`github/repos/${repo}/contents/${caminho}`);
    if (Array.isArray(r)) await listaArquivos(el, repo, caminho, r);
    else await viewerArquivo(el, repo, r);
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      head({ title: "Files", sub: "Browse and manage stored files" }) +
      errorBox(err.source ?? "GitHub", err.message, err.next ?? "tente de novo");
  }
};

async function listaArquivos(el: HTMLElement, repo: string, caminho: string, entradas: FsEntry[]): Promise<void> {
  const ordenadas = [...entradas].sort((a, b) =>
    a.type === b.type ? a.name.localeCompare(b.name) : a.type === "dir" ? -1 : 1,
  );

  // Modified: a contents API não traz data — uma chamada de commits por
  // arquivo visível, derivada do último commit que toca o path (contrato).
  // ponytail: teto de 50 linhas; acima disso a coluna fica "–".
  const datas = new Map(
    await Promise.all(
      ordenadas
        .filter((e) => e.type === "file")
        .slice(0, 50)
        .map(async (e) => {
          const cs = await gateway<FsCommit[]>(
            `github/repos/${repo}/commits?path=${encodeURIComponent(e.path)}&per_page=1`,
          ).catch(() => [] as FsCommit[]);
          return [e.path, cs[0]?.commit.committer.date ?? ""] as const;
        }),
    ),
  );

  const header = head({
    title: "Files",
    sub: "Browse and manage stored files",
    actions: `<button class="btn" id="files-pasta" type="button">New Folder</button>
              <button class="btn" id="files-novo" type="button">New File</button>
              <button class="btn btn-primary" id="files-envio" type="button">Upload</button>
              <input type="file" id="files-input" hidden>`,
  });

  const linhas = ordenadas.map((e) => {
    const nome = `<a class="file-link" href="#/arquivos/${esc(e.path)}">${e.type === "dir" ? ICO_PASTA : ICO_ARQ}${esc(e.name)}</a>`;
    // Toda linha tem "···" (spec 16-files, f025). Download/Rename/Delete da
    // contents API são por arquivo (DELETE e download_url não existem para
    // diretório), então a pasta leva só o que tem dono: navegar e copiar path.
    const menu =
      e.type === "file"
        ? `<details class="files-menu">
             <summary aria-label="Ações de ${esc(e.name)}">···</summary>
             <div class="files-menu-list">
               ${e.download_url ? `<a href="${esc(e.download_url)}" download="${esc(e.name)}" target="_blank" rel="noopener">Download</a>` : ""}
               <button type="button" data-act="rename" data-path="${esc(e.path)}" data-sha="${esc(e.sha)}" data-name="${esc(e.name)}">Rename</button>
               <button type="button" data-act="delete" data-path="${esc(e.path)}" data-sha="${esc(e.sha)}" data-name="${esc(e.name)}">Delete</button>
             </div>
           </details>`
        : `<details class="files-menu">
             <summary aria-label="Ações de ${esc(e.name)}">···</summary>
             <div class="files-menu-list">
               <a href="#/arquivos/${esc(e.path)}">Open</a>
               <button type="button" data-act="copy" data-path="${esc(e.path)}">Copy path</button>
             </div>
           </details>`;
    return `<tr>
      <td>${nome}</td>
      <td class="num">${e.type === "dir" ? "–" : kb(e.size)}</td>
      <td class="mono">${e.type === "dir" ? "–" : esc(fmtData(datas.get(e.path) || null))}</td>
      <td class="num">${menu}</td>
    </tr>`;
  });

  // Pill "Global" existe no spec mas o escopo está sem dono (issue #8 no
  // brief): fica visível e desabilitada — presente, sem fingir funcionar.
  el.innerHTML =
    header +
    `<div class="files-bar">
       <div class="files-scope" role="tablist">
         <button class="files-scope-pill" role="tab" aria-selected="true" type="button">Project</button>
         <button class="files-scope-pill" role="tab" aria-selected="false" type="button" disabled
                 title="Escopo global ainda sem dono definido (issue #8)">Global</button>
       </div>
       ${crumbLista(caminho)}
       <button class="files-copy" id="files-cp" type="button" title="Copiar caminho" aria-label="Copiar caminho">⧉</button>
     </div>` +
    (ordenadas.length
      ? table(["Name", "Size", "Modified", ""], linhas)
      : empty("Pasta vazia", "Nada aqui ainda. Crie um arquivo ou envie um."));

  posicionaMenus(el);

  el.querySelector<HTMLButtonElement>("#files-cp")!.onclick = () =>
    void navigator.clipboard.writeText(caminho || "/").then(() => toast("Caminho copiado"));

  const btnPasta = el.querySelector<HTMLButtonElement>("#files-pasta")!;
  btnPasta.onclick = () => {
    const nome = prompt("Nome da nova pasta")?.trim();
    if (!nome) return;
    void agir(btnPasta, async () => {
      // git não versiona pasta vazia: "criar pasta" = criar .gitkeep dentro
      await escreve(repo, junta(caminho, nome, ".gitkeep"), paraB64(""));
      toast(`Pasta ${nome} criada`);
      await files(el);
    });
  };

  const btnNovo = el.querySelector<HTMLButtonElement>("#files-novo")!;
  btnNovo.onclick = () => {
    const nome = prompt("Nome do novo arquivo")?.trim();
    if (!nome) return;
    void agir(btnNovo, async () => {
      const path = junta(caminho, nome);
      await escreve(repo, path, paraB64(""));
      toast(`${nome} criado`);
      location.hash = `/arquivos/${path}`; // abre direto no viewer
    });
  };

  const input = el.querySelector<HTMLInputElement>("#files-input")!;
  const btnEnvio = el.querySelector<HTMLButtonElement>("#files-envio")!;
  btnEnvio.onclick = () => input.click();
  input.onchange = () => {
    const arq = input.files?.[0];
    if (!arq) return;
    void agir(btnEnvio, async () => {
      // Lido no cliente como data URL: preserva binário sem passar por texto.
      const b64 = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(",")[1] ?? "");
        fr.onerror = () => rej(new SourceError("Files", "não consegui ler o arquivo", "tente de novo"));
        fr.readAsDataURL(arq);
      });
      await escreve(repo, junta(caminho, arq.name), b64);
      toast(`${arq.name} enviado`);
      await files(el);
    });
  };

  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-act]")) {
    b.onclick = () => {
      const act = b.dataset.act ?? "";
      const path = b.dataset.path ?? "";
      const sha = b.dataset.sha ?? "";
      const nome = b.dataset.name ?? "";

      // Mesmo clipboard do ⧉ do breadcrumb.
      if (act === "copy") {
        void navigator.clipboard.writeText(path).then(() => toast("Caminho copiado"));
        return;
      }

      if (act === "delete") {
        if (!confirm(`Delete ${nome}?`)) return;
        void agir(b, async () => {
          await apaga(repo, path, sha, `HOPE: apaga ${path}`);
          toast(`${nome} apagado`);
          // Reler aqui trazia o arquivo de volta: a contents API responde
          // `Cache-Control: private, max-age=60` e serve a listagem anterior
          // por até um minuto. Como o app não faz polling, esse estado velho
          // ficava congelado na tela. O DELETE já é a confirmação da fonte —
          // então a lista repinta do que está em mãos, sem a linha apagada.
          await listaArquivos(el, repo, caminho, ordenadas.filter((x) => x.path !== path));
        });
      }

      if (act === "rename") {
        const novo = prompt("Novo nome", nome)?.trim();
        if (!novo || novo === nome) return;
        void agir(b, async () => {
          // A contents API não tem rename atômico: PUT no path novo + DELETE
          // no antigo, nesta ordem — se o PUT falhar, nada se perde.
          const arq = await gateway<FsEntry>(`github/repos/${repo}/contents/${path}`);
          if (arq.encoding !== "base64" || typeof arq.content !== "string") {
            throw new SourceError("GitHub", "arquivo grande demais para renomear pelo app", "renomeie via git");
          }
          const dir = path.split("/").slice(0, -1).join("/");
          await escreve(repo, junta(dir, novo), arq.content.replace(/\s/g, ""));
          await apaga(repo, path, sha, `HOPE: renomeia ${path} → ${novo}`);
          toast(`Renomeado para ${novo}`);
          await files(el);
        });
      }
    };
  }
}

async function viewerArquivo(el: HTMLElement, repo: string, f: FsEntry): Promise<void> {
  // Metadados derivados do último commit que toca o path: data em "Modified",
  // autoria no fim ("agent:…" quando o agente commita com identidade própria).
  const cs = await gateway<FsCommit[]>(
    `github/repos/${repo}/commits?path=${encodeURIComponent(f.path)}&per_page=1`,
  ).catch(() => [] as FsCommit[]);
  const c = cs[0];
  const autor = c ? (c.author?.login ?? c.commit.author.name) : "";
  const meta = [kb(f.size), `Modified ${fmtData(c?.commit.committer.date ?? null)}`, autor]
    .filter(Boolean)
    .join(" · ");

  const pai = f.path.split("/").slice(0, -1).join("/");
  const voltar = pai ? `#/arquivos/${esc(pai)}` : "#/arquivos";
  const editavel = f.encoding === "base64" && typeof f.content === "string";

  const acoes = editavel
    ? `<button class="btn" id="files-preview" type="button" aria-pressed="false">Preview</button>
       <button class="btn" id="files-download" type="button">Download</button>
       <button class="btn btn-primary" id="files-save" type="button">Save</button>`
    : f.download_url
      ? `<a class="btn" href="${esc(f.download_url)}" download="${esc(f.name)}" target="_blank" rel="noopener">Download</a>`
      : "";

  el.innerHTML = `
    <div class="files-bar">
      <a class="back files-guard" href="${voltar}" aria-label="Voltar">←</a>
      ${crumbViewer(f.path)}
      <button class="files-copy" id="files-cp" type="button" title="Copiar caminho" aria-label="Copiar caminho">⧉</button>
      <div class="files-acoes">${acoes}</div>
    </div>
    <div class="files-meta">${esc(meta)}</div>
    ${
      editavel
        ? `<div class="files-editor" id="files-ed">
             <pre class="files-gutter" id="files-gutter">1</pre>
             <textarea class="files-ta" id="files-ta" wrap="off" spellcheck="false" aria-label="Conteúdo do arquivo"></textarea>
           </div>
           <div class="card" id="files-pv" hidden><div class="card-body" id="files-pv-corpo"></div></div>`
        : // ponytail: acima de 1 MB a contents API não manda base64 e o gateway
          // só fala JSON — sem editor; o download_url continua servindo.
          empty("Arquivo grande demais para o editor", "A contents API só entrega o conteúdo em base64 até 1 MB.")
    }`;

  el.querySelector<HTMLButtonElement>("#files-cp")!.onclick = () =>
    void navigator.clipboard.writeText(f.path).then(() => toast("Caminho copiado"));

  if (!editavel) return;

  const ta = el.querySelector<HTMLTextAreaElement>("#files-ta")!;
  const gutter = el.querySelector<HTMLElement>("#files-gutter")!;
  const dot = el.querySelector<HTMLElement>("#files-dot")!;
  let original = deB64(f.content ?? "");
  let sha = f.sha;
  ta.value = original;

  const numera = () => {
    const n = ta.value.split("\n").length;
    gutter.textContent = Array.from({ length: n }, (_, i) => String(i + 1)).join("\n");
  };
  numera();
  ta.oninput = () => {
    numera();
    dot.hidden = ta.value === original;
  };
  ta.onscroll = () => {
    gutter.scrollTop = ta.scrollTop;
  };

  // Sair com edição não salva: mesmo dialog nativo do original (f029).
  for (const a of el.querySelectorAll<HTMLAnchorElement>(".files-guard")) {
    a.onclick = (ev) => {
      if (!dot.hidden && !confirm("You have unsaved changes. Leave anyway?")) ev.preventDefault();
    };
  }

  // Preview: render de markdown no cliente — o blob já está aqui.
  const pv = el.querySelector<HTMLElement>("#files-pv")!;
  const pvCorpo = el.querySelector<HTMLElement>("#files-pv-corpo")!;
  const ed = el.querySelector<HTMLElement>("#files-ed")!;
  const btnPv = el.querySelector<HTMLButtonElement>("#files-preview")!;
  btnPv.onclick = () => {
    const abrir = pv.hidden;
    if (abrir) pvCorpo.innerHTML = md(ta.value);
    pv.hidden = !abrir;
    ed.hidden = abrir;
    btnPv.setAttribute("aria-pressed", String(abrir));
  };

  el.querySelector<HTMLButtonElement>("#files-download")!.onclick = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([ta.value], { type: "text/plain" }));
    a.download = f.name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const btnSave = el.querySelector<HTMLButtonElement>("#files-save")!;
  btnSave.onclick = () =>
    void (async () => {
      btnSave.disabled = true;
      try {
        // Manda o sha lido no GET: se o arquivo mudou por baixo, o GitHub
        // recusa em vez de sobrescrever calado.
        sha = await escreve(repo, f.path, paraB64(ta.value), sha);
        original = ta.value;
        dot.hidden = true;
        toast("Salvo");
      } catch (e) {
        toast((e as SourceError).message);
      } finally {
        btnSave.disabled = false;
      }
    })();
}

// ------------------------------------------------------------------- Repos

/** Rotas: #/repos (lista), #/repos/<nome> (Edit Repo, aba General) e
 *  #/repos/<nome>/env (aba Environment). */
export const repos = async (el: HTMLElement): Promise<void> => {
  const partes = decodeURIComponent(location.hash.replace(/^#\/?/, "")).split("/");
  const nome = partes[1] ?? "";
  if (nome) return editRepo(el, nome, partes[2] === "env");

  // "+ Add Repo", coluna Credential e Delete do ⋮ ficam de fora: a lista é a
  // conta GitHub inteira e nenhuma API nativa guarda "conexão" ou credencial
  // nomeada por repo (contrato 17-repos, sem_dono).
  const header = head({ title: "Repos", sub: "Manage GitHub repository connections for your agents" });
  el.innerHTML = header + skeleton();
  try {
    const rs = await github.repos();
    const linhas = rs.map(
      (r) => `
      <tr>
        <td><a class="files-repo-name" href="#/repos/${esc(r.name)}">${esc(r.name)}</a></td>
        <td class="mono">${esc(r.full_name)}</td>
        <td class="mono">${esc(fmtData(r.updated_at))}</td>
        <td class="num">
          <details class="files-menu">
            <summary aria-label="Ações de ${esc(r.name)}">⋮</summary>
            <div class="files-menu-list"><a href="#/repos/${esc(r.name)}">Edit</a></div>
          </details>
        </td>
      </tr>`,
    );
    el.innerHTML =
      header +
      (rs.length
        ? table(["Name", "URL", "Updated", ""], linhas)
        : empty("Nenhum repositório", "A conta GitHub conectada não tem repositórios."));
    posicionaMenus(el);
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "GitHub", err.message, err.next ?? "tente de novo");
  }
};

async function editRepo(el: HTMLElement, nome: string, env: boolean): Promise<void> {
  // "←" e "Cancel" descartam o estado local e voltam à lista — sem chamada.
  const header = `
    <header class="page-head">
      <div style="display:flex;align-items:center;gap:12px">
        <a class="btn" href="#/repos" aria-label="Voltar">←</a>
        <h1 class="page-title">Edit Repo</h1>
      </div>
      <div class="page-actions"><a class="btn" href="#/repos">Cancel</a></div>
    </header>`;
  el.innerHTML = header + skeleton();
  try {
    const rs = await github.repos();
    const r = rs.find((x) => x.name === nome);
    if (!r) {
      el.innerHTML = header + empty("Repo não encontrado", `"${nome}" não está entre os repositórios da conta.`);
      return;
    }
    const abas = tabs([
      { label: "General", href: `#/repos/${nome}`, ativa: !env },
      { label: "Environment", href: `#/repos/${nome}/env`, ativa: env },
    ]);
    const painel = env ? await painelEnvironment(r) : painelGeneral(r);
    el.innerHTML = header + abas + `<div class="files-panel">${painel}</div>`;
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "GitHub", err.message, err.next ?? "tente de novo");
  }
}

/** Aba General (INFERIDA no spec, nunca aberta em frame): só o que tem dono —
 *  URL do payload já carregado e mount path derivado da convenção. Campo de
 *  credencial fica de fora (sem dono; o PAT é único da conta). */
function painelGeneral(r: Repo): string {
  return `
    <div class="card"><div class="card-body">
      <div class="field">
        <label class="label" for="repo-url">Repository</label>
        <input class="input" id="repo-url" value="${esc(r.full_name)}" readonly />
      </div>
      <div class="field">
        <label class="label" for="repo-mount">Mount path</label>
        <input class="input" id="repo-mount" value="/workspace/${esc(r.name)}" readonly />
        <span class="hint">Derivado da convenção /workspace/&lt;repo&gt;; a montagem em si é do runner.</span>
      </div>
      <p class="hint">Credencial: todos os repos usam o PAT único da conta — não existe associação nomeada repo↔credencial em API nativa.</p>
    </div></div>`;
}

/** Aba Environment: os .env são Actions Secrets. A API devolve só nome e
 *  datas — valor de secret nunca é legível de volta. */
async function painelEnvironment(r: Repo): Promise<string> {
  const s = await gateway<{ total_count: number; secrets: Array<{ name: string; updated_at: string }> }>(
    `github/repos/${r.full_name}/actions/secrets`,
  );
  const corpo = s.total_count
    ? table(
        ["Name", "Updated"],
        s.secrets.map(
          (x) => `<tr><td class="mono">${esc(x.name)}</td><td class="mono">${esc(fmtData(x.updated_at))}</td></tr>`,
        ),
      )
    : `<div class="files-env-empty">No environment files configured.</div>`;
  return `
    <div class="card"><div class="card-body">
      <p class="files-env-note">Environment files are mounted relative to /workspace/${esc(r.name)}/ in agent sessions. Values are encrypted at rest.</p>
      ${corpo}
      <p class="hint">Gravar um secret exige cifrar o valor no navegador com libsodium (sealed box da public key
      do repo) — dependência que este app não tem. Até essa decisão, use <code>gh secret set</code>; por isso
      "+ Add .env file" e "Save All" ficam de fora em vez de fingir funcionar.</p>
    </div></div>`;
}

// Checagem mínima do par base64 UTF-8 — se quebrar, Save corrompe acento.
if (import.meta.env.DEV) {
  console.assert(deB64(paraB64("acentuação ✓ ●")) === "acentuação ✓ ●", "files: base64 UTF-8 quebrou");
}
